<script setup lang="ts">
import type { GuideDataAvailability, GuideDataStatus } from '@/onboarding/dataContext'
import type { BackendGuideRouteRecord } from '@/onboarding/routes'
import type { GuideRuntimeFailure, GuideRuntimeResult } from '@/onboarding/runtime'
import type { GuideStep, GuideTarget } from '@/onboarding/types'
import {
  evaluateGuideStepData,
  getGuideDataRule,
  isDeferredAvailability,
} from '@/onboarding/dataContext'
import { resolveGuideRouteRecordDetailed } from '@/onboarding/routes'
import {
  guideRuntimeFailure,
  guideRuntimeSuccess,
  isGuideRuntimeFailure,
  waitForGuidePageMounted,
} from '@/onboarding/runtime'
import useMenuStore from '@/store/modules/menu'
import useOnboardingStore from '@/store/modules/onboarding'
import useRouteStore from '@/store/modules/route'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const onboardingStore = useOnboardingStore()
const routeStore = useRouteStore()
const menuStore = useMenuStore()
const route = useRoute()
const router = useRouter()
const { locale, t } = useI18n()

const targetRect = ref<DOMRect | null>(null)
const targetElement = ref<HTMLElement | null>(null)
const cardElement = ref<HTMLElement | null>(null)
const cardSize = ref({ width: 0, height: 0 })
const viewportBounds = ref({
  left: 0,
  top: 0,
  width: window.innerWidth,
  height: window.innerHeight,
})
const ready = ref(false)
const dataAvailability = ref<GuideDataAvailability>({ status: 'ready', targetCandidates: [] })
const progressSubmitting = ref(false)
const progressBusy = computed(() => progressSubmitting.value || onboardingStore.remoteSyncing)
const runtimeError = ref<GuideRuntimeFailure | null>(null)
let prepareGeneration = 0
let guideNavigationTarget = ''
let runningBeforeAction = false
let guideDetailReturnRoute = ''
let cardResizeObserver: ResizeObserver | null = null
let targetResizeObserver: ResizeObserver | null = null
let dataStateMutationObserver: MutationObserver | null = null
const guideDetailSurfaces = new Set<HTMLElement>()

const businessSurfaceSelectors = [
  '.el-dialog:not([style*="display: none"])',
  '.el-drawer:not([style*="display: none"])',
  '.cr-drawer',
  '.coop-detail-drawer',
]

// 当列表没有可操作行或当前账号没有编辑权限时，教程仍应停留在正确业务页面，
// 用该页面的只读列表作为说明目标，而不是误报“找不到页面”。
const stepTargetFallbackSelectors: Record<string, string[]> = {
  'org-role-menu': ['[data-guide-id="organization-role-table"]'],
  'org-role-permissions': ['[data-guide-id="organization-role-table"]'],
  'org-role-diagnostics': ['[data-guide-id="organization-role-table"]'],
}

const selectors: Record<GuideTarget, string[]> = {
  page: ['.main-page', '.main-container', '.layout-main', '.fa-page-main', '#app'],
  header: ['.topbar-container', '.topbar', 'header'],
  sidebar: ['.main-sidebar-container', '.sub-sidebar-container', 'aside'],
  search: ['[class*="nav-search"]', '[class*="search"] .el-input', '.el-input'],
  account: ['.user-trigger'],
  title: ['.fa-page-header', '.page-header', '.page-title', 'main h1', 'main h2'],
  filters: ['.fa-search-bar', '[class*="filter"] .el-form', '[class*="search"] .el-form', 'main .el-form'],
  table: ['.el-table', 'main table', 'main [class*="table-wrap"]', 'main [class*="list-wrap"]'],
  primary: ['main .el-button--primary', '.el-button--primary'],
  tabs: ['.el-tabs', 'main [class*="tabs"]', 'main [role="tablist"]'],
  form: ['main .el-form', 'main [class*="form-wrap"]', '.el-form'],
  stats: ['main [class*="stat"]', '.fa-page-main [class*="data-card"]', 'main [class*="chart"]', 'main .el-row .el-card'],
  toolbar: ['.fa-page-main [class*="toolbar"]', 'main [class*="toolbar"]', '.fa-page-main .el-button-group', 'main .el-space', 'main [class*="actions"]'],
  pagination: ['.el-pagination'],
}

const fallbackStepTypes: Record<GuideTarget, string> = {
  page: 'overview',
  header: 'workflow',
  sidebar: 'navigation',
  search: 'search',
  account: 'account',
  title: 'overview',
  filters: 'filters',
  table: 'table',
  primary: 'create',
  tabs: 'tabs',
  form: 'identity',
  stats: 'kpi',
  toolbar: 'risk',
  pagination: 'table',
}

const moduleName = computed(() => onboardingStore.currentTask
  ? t(onboardingStore.currentTask.moduleKey)
  : '')

const hasDataConstraint = computed(() => dataAvailability.value.status !== 'ready')

function dataStateTranslationKey(status: GuideDataStatus) {
  return status.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

const stepTitle = computed(() => onboardingStore.currentStep
  ? hasDataConstraint.value
    ? locale.value.toLowerCase() === 'zh-cn' && isDeferredAvailability(dataAvailability.value)
      ? dataAvailability.value.title
      : t(`onboarding.dataState.${dataStateTranslationKey(dataAvailability.value.status)}.title`, { module: moduleName.value })
    : locale.value.toLowerCase() === 'zh-cn'
      ? onboardingStore.currentStep.title
      : t(`onboarding.stepTypes.${fallbackStepTypes[onboardingStore.currentStep.target]}.title`, { module: moduleName.value })
  : '')

const stepDescription = computed(() => onboardingStore.currentStep
  ? hasDataConstraint.value
    ? locale.value.toLowerCase() === 'zh-cn' && isDeferredAvailability(dataAvailability.value)
      ? dataAvailability.value.description
      : t(`onboarding.dataState.${dataStateTranslationKey(dataAvailability.value.status)}.description`, { module: moduleName.value })
    : locale.value.toLowerCase() === 'zh-cn'
      ? onboardingStore.currentStep.description
      : t(`onboarding.stepTypes.${fallbackStepTypes[onboardingStore.currentStep.target]}.description`, { module: moduleName.value })
  : '')

const dataStateBadge = computed(() => hasDataConstraint.value
  ? t(`onboarding.dataState.${dataStateTranslationKey(dataAvailability.value.status)}.badge`)
  : '')

const taskTitle = computed(() => onboardingStore.currentTask
  ? locale.value.toLowerCase() === 'zh-cn'
    ? onboardingStore.currentTask.title
    : t(onboardingStore.currentTask.titleKey, { edition: t(onboardingStore.edition.nameKey) })
  : '')

function normalizeMenuValue(value: unknown) {
  return String(value || '')
    .replace(/\s+/g, '')
    .toLowerCase()
}

function menuEntryContainsSurveyStation(menu: any): boolean {
  // 菜单标题来自 routeList 接口；这里仅按“调查站”业务语义识别主导航，
  // 不保存或判断任何固定 path/fullPath/component/name/menuId。
  const title = normalizeMenuValue(menu?.meta?.title)
  if (
    title.includes('调查站')
    || title.includes('surveysite')
    || title.includes('surveystation')
  ) {
    return true
  }
  return Array.isArray(menu?.children)
    && menu.children.some((child: any) => menuEntryContainsSurveyStation(child))
}

function findSurveyStationMenuIndex() {
  return menuStore.allMenus.findIndex(menu => menuEntryContainsSurveyStation(menu))
}

async function activateSurveyStationMenu(timeout = 8000) {
  const startedAt = Date.now()
  let stationMenuIndex = findSurveyStationMenuIndex()
  while (stationMenuIndex < 0 && Date.now() - startedAt < timeout) {
    await new Promise(resolve => window.setTimeout(resolve, 120))
    stationMenuIndex = findSurveyStationMenuIndex()
  }
  if (stationMenuIndex < 0) {
    return false
  }

  if (menuStore.actived !== stationMenuIndex) {
    menuStore.setActived(stationMenuIndex)
    await nextTick()
  }

  // 等待次级侧栏真正切换到调查站菜单后，再计算第一步高亮区域。
  await waitForElement(
    () => document.querySelector<HTMLElement>('.sub-sidebar-container.survey-station-menu'),
    2400,
  )
  return true
}

function isElementVisible(element: HTMLElement | null | undefined) {
  if (!element || !element.isConnected) {
    return false
  }
  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) {
    return false
  }
  const style = window.getComputedStyle(element)
  return style.display !== 'none'
    && style.visibility !== 'hidden'
    && style.visibility !== 'collapse'
    && style.opacity !== '0'
}

function findVisibleElement(queryList: string[], scope: ParentNode = document) {
  for (const query of queryList) {
    let elements: HTMLElement[] = []
    try {
      elements = Array.from(scope.querySelectorAll<HTMLElement>(query))
    }
    catch {
      continue
    }
    const element = elements.find(item => isElementVisible(item))
    if (element) {
      return element
    }
  }
  return null
}

function currentBusinessPageRoot() {
  const main = document.querySelector<HTMLElement>('#app-main .main-container .main')
  if (!main) {
    return null
  }
  const visibleChildren = Array.from(main.querySelectorAll<HTMLElement>(':scope > *'))
    .filter(item => isElementVisible(item) && !item.classList.contains('exit-main-page-maximize'))
  return visibleChildren.at(-1) || (isElementVisible(main) ? main : null)
}

function decorateRuntimeStepTargets(step: GuideStep) {
  if (onboardingStore.edition.id !== 'sample-library') {
    return
  }
  const pageRoot = currentBusinessPageRoot()
  if (!pageRoot) {
    return
  }

  if (step.pageKey === 'finance.employee.log') {
    const target = pageRoot.matches('.employee-finance-log-page')
      ? pageRoot
      : pageRoot.querySelector<HTMLElement>('.employee-finance-log-page')
    target?.setAttribute('data-guide-id', 'finance-employee-log-page')
  }
  else if (step.pageKey === 'finance.employee.bill') {
    const target = pageRoot.matches('.supplier-finance-log-page')
      ? pageRoot
      : pageRoot.querySelector<HTMLElement>('.supplier-finance-log-page')
    target?.setAttribute('data-guide-id', 'finance-employee-bill-page')
  }
}

function detailSurfaceSelectors(target: GuideTarget) {
  const surfaces = businessSurfaceSelectors
  const contents: Partial<Record<GuideTarget, string[]>> = {
    tabs: ['.el-tabs'],
    table: ['.el-table'],
    filters: ['.el-form'],
    form: ['.el-form'],
    primary: ['.el-button--primary'],
  }
  return [
    ...(contents[target] || []).flatMap(content => surfaces.map(surface => `${surface} ${content}`)),
    ...surfaces,
  ]
}

function findTarget() {
  const step = onboardingStore.currentStep
  if (!step) {
    return null
  }
  if (hasDataConstraint.value) {
    return findVisibleElement(dataAvailability.value.targetCandidates)
      || currentBusinessPageRoot()
  }

  // 三个版本的“认识业务导航”第一步统一圈出最上方横向业务导航。
  // 不允许匹配侧栏、Topbar、标签栏或右侧账号工具区。
  if (step.id === 'top-nav') {
    return findVisibleElement([
      'header [data-guide-id="top-nav"]',
      'header .business-top-navigation',
      'header .menu-container',
    ])
  }

  // 原教程已经为每一步声明了准确的 target。只要存在明确选择器，
  // 就必须高亮该业务模块，不能退化成整张表格、整个弹窗或整页兜底，
  // 否则看似有高亮，实际讲解区域会发生偏移。
  if (step.selectors.length) {
    const pageRoot = step.surface === 'list' ? currentBusinessPageRoot() : null
    const exactTarget = (pageRoot ? findVisibleElement(step.selectors, pageRoot) : null)
      || findVisibleElement(step.selectors)
    if (exactTarget) {
      return exactTarget
    }
    // 真实资源列表为空时不会渲染详情按钮和详情抽屉。
    // 此时高亮完整资源市场模块，避免把“暂无业务数据”误报为“找不到页面”。
    if (/^cooperation-(?:supplier|customer)-detail$/.test(step.id)) {
      return findVisibleElement(['[data-guide-id="cooperation-market-module"]'])
    }
    const fallbackSelectors = stepTargetFallbackSelectors[step.id]
    if (fallbackSelectors) {
      return findVisibleElement(fallbackSelectors)
    }
    return null
  }

  const semanticTarget = findSemanticTarget(step)
  if (semanticTarget) {
    return semanticTarget
  }
  const surfaceSelectors = step.surface === 'detail' ? detailSurfaceSelectors(step.target) : []
  return findVisibleElement([...surfaceSelectors, ...selectors[step.target]])
}

/**
 * 抽屉、弹窗步骤只讲解中间业务内容，不应把标题和底部操作按钮纳入高亮范围。
 * 页面已经提供更精确的内容标记时会直接使用该标记；这里只处理仍指向 Element 外壳的兜底目标。
 */
function resolveSurfaceContentTarget(element: HTMLElement) {
  const isDrawerOrDialog = element.matches('.el-drawer, .el-dialog')
  if (!isDrawerOrDialog) {
    return element
  }
  // 覆盖层外壳只负责承载标题、内容和操作按钮。教程统一取中间 body，
  // 不根据异步子节点的临时高度猜测内容范围，也不会把 header/footer 圈进去。
  return element.querySelector<HTMLElement>('.el-drawer__body, .el-dialog__body')
    || element
}

function findSafeOpenControl(selector: string) {
  const normalized = selector.toLowerCase()
  const keywordGroups = [
    { test: /analysis/, words: ['分析', 'analysis'] },
    { test: /expand/, words: ['展开', 'expand'] },
    { test: /export/, words: ['导出', 'export'] },
    { test: /risk|safety/, words: ['批量支付', '支付', '拒绝', '开票', '作废', '下载'] },
    { test: /actions?/, words: ['保存', '确认', '提交'] },
    { test: /detail|drawer-open|menu-open/, words: ['查看详情', '详情', '查看', '编辑', '菜单', 'detail', 'view', 'edit'] },
    { test: /new|create|add|invite|apply/, words: ['新增', '新建', '创建', '添加', '邀请', '申请', 'new', 'create', 'add', 'invite', 'apply'] },
    { test: /profile/, words: ['对外资料', '基本资料', '我的资料', '资料', 'profile'] },
    { test: /sent|application/, words: ['合作申请', '申请', 'application'] },
    { test: /partner/, words: ['合作关系', '合作伙伴', '合作', 'partner'] },
    { test: /point/, words: ['积分', 'point'] },
    { test: /bill/, words: ['账单', 'bill'] },
    { test: /role-menu/, words: ['菜单权限', '菜单', 'menu'] },
    { test: /permission|diagnostic/, words: ['操作权限', '权限', 'permission'] },
    { test: /integration/, words: ['配置', '查看', '编辑', 'configure'] },
  ]
  const words = keywordGroups.find(group => group.test.test(normalized))?.words || []
  if (!words.length) {
    return null
  }
  const controls = Array.from(document.querySelectorAll<HTMLElement>('button, a, [role="button"]'))
  return controls.find(control => (
    isElementVisible(control)
    && !control.hasAttribute('disabled')
    && words.some(word => control.textContent?.replace(/\s+/g, '').toLowerCase().includes(word))
  )) || null
}

function findSemanticTarget(step: GuideStep) {
  if (
    step.target === 'primary'
    || /expand/.test(step.id)
    || (step.target === 'toolbar' && /export|risk|safety|actions?/.test(step.id))
  ) {
    return findSafeOpenControl(`${step.id} ${step.title}`)
  }
  if (step.target === 'tabs') {
    return findSafeTab(`${step.id} ${step.title}`)
  }
  return findSemanticTextTarget(step)
}

function semanticTextWords(step: GuideStep) {
  const normalized = `${step.id} ${step.title}`.toLowerCase()
  const keywordGroups = [
    { test: /identity/, words: ['项目名称', '客户名称', '账户名称', '主体', '名称'] },
    { test: /commercial/, words: ['CPI', '价格', '供应商'] },
    { test: /metrics/, words: ['LOI', 'IR', '配额'] },
    { test: /supplement/, words: ['备注', '附件', '说明'] },
    { test: /device/, words: ['设备', '桌面', '手机'] },
    { test: /mutex/, words: ['互斥'] },
    { test: /screener/, words: ['前置筛选', '筛选'] },
    { test: /internal/, words: ['内部检测', '内部'] },
    { test: /hourly/, words: ['每小时'] },
    { test: /security/, words: ['安检', '安全'] },
    { test: /settlement/, words: ['结算'] },
    { test: /assignment/, words: ['分配'] },
    { test: /control/, words: ['项目控制', '控制'] },
    { test: /regions?/, words: ['区域'] },
    { test: /clients?/, words: ['客户'] },
    { test: /finance-account/, words: ['收款', '账户'] },
    { test: /callback/, words: ['回调'] },
    { test: /invoice/, words: ['发票'] },
    { test: /profile/, words: ['对外资料', '基本资料', '资料'] },
    { test: /applications?|sent/, words: ['合作申请', '申请'] },
    { test: /partners?/, words: ['合作关系', '合作伙伴'] },
    { test: /points?/, words: ['积分'] },
    { test: /bills?/, words: ['账单'] },
    { test: /wallet/, words: ['钱包'] },
    { test: /analysis/, words: ['数据分析', '分析', '排行'] },
    { test: /market/, words: ['合作资源', '资源列表', '供应商', '客户'] },
    { test: /access/, words: ['访问', '注册', '验证'] },
    { test: /display/, words: ['显示', '展示'] },
    { test: /protocol/, words: ['协议'] },
    { test: /permission/, words: ['权限'] },
    { test: /manager/, words: ['主管'] },
    { test: /employees?/, words: ['员工'] },
    { test: /conditions?/, words: ['条件'] },
    { test: /preview/, words: ['预览'] },
    { test: /credentials?/, words: ['凭据', '密钥'] },
    { test: /test/, words: ['测试'] },
    { test: /performance/, words: ['速度', '超时'] },
  ]
  return keywordGroups.find(group => group.test.test(normalized))?.words || []
}

function findSemanticTextTarget(step: GuideStep) {
  const words = semanticTextWords(step)
  if (!words.length) {
    return null
  }
  const detailSurface = step.surface === 'detail'
    ? findVisibleElement(businessSurfaceSelectors)
    : null
  const scope = detailSurface || findVisibleElement(['main', '.fa-page-main']) || document.body
  const candidates = Array.from(scope.querySelectorAll<HTMLElement>([
    '.el-form-item__label',
    '.el-tabs__item',
    '.el-card__header',
    '.el-collapse-item__header',
    '.el-descriptions__label',
    'th',
    'label',
    'h1',
    'h2',
    'h3',
    'h4',
    '[class*="title"]',
  ].join(',')))
  const label = candidates.find(candidate => (
    isElementVisible(candidate)
    && words.some(word => candidate.textContent?.replace(/\s+/g, '').toLowerCase().includes(word.toLowerCase()))
  ))
  return label?.closest<HTMLElement>(
    '.el-form-item, .el-tab-pane, .el-card, .el-collapse-item, .el-descriptions__cell, tr, section',
  ) || label || null
}

function findSafeTab(selector: string) {
  const normalized = selector.toLowerCase()
  const keywordGroups = [
    { test: /base|basic/, words: ['基础', '基本'] },
    { test: /settlement|finance/, words: ['结算', '财务'] },
    { test: /risk|security/, words: ['风控', '风险', '安全'] },
    { test: /quota/, words: ['配额'] },
    { test: /callback/, words: ['回调'] },
    { test: /invoice/, words: ['发票'] },
    { test: /permission/, words: ['权限'] },
    { test: /supplier/, words: ['供应商'] },
    { test: /site|web/, words: ['站点', '网站'] },
    { test: /profile/, words: ['资料'] },
    { test: /sent|application/, words: ['申请'] },
    { test: /partner/, words: ['合作'] },
    { test: /point/, words: ['积分'] },
    { test: /bill/, words: ['账单'] },
    { test: /sms/, words: ['短信'] },
    { test: /email/, words: ['邮件'] },
  ]
  const words = keywordGroups.find(group => group.test.test(normalized))?.words || []
  if (!words.length) {
    return null
  }
  const tabs = Array.from(document.querySelectorAll<HTMLElement>(
    '.el-tabs__item, [role="tab"], .cr-my-tabs button',
  ))
  return tabs.find(tab => (
    isElementVisible(tab)
    && words.some(word => tab.textContent?.replace(/\s+/g, '').toLowerCase().includes(word))
  )) || null
}

async function activateGuideTab(selector: string, timeout: number) {
  const tabContent = await waitForElement(
    () => findVisibleElement([selector]) || findSafeTab(selector),
    timeout,
  )
  if (!tabContent) {
    return false
  }

  // data-guide-id 通常写在 Element Plus 页签 label 的 span 上。
  // 点击真正的 role=tab / el-tabs__item，避免 span.click() 在部分版本中
  // 没有更新 v-model，进而使页签内容始终不渲染。
  const tab = tabContent.closest<HTMLElement>('.el-tabs__item, [role="tab"]') || tabContent
  tab.click()
  await nextTick()

  await waitForElement(() => {
    const active = tab.classList.contains('is-active')
      || tab.getAttribute('aria-selected') === 'true'
    return active ? tab : null
  }, Math.min(timeout, 2400))
  return true
}

async function waitForElement(findElement: () => HTMLElement | null, timeout = 2400) {
  const existingElement = findElement()
  if (existingElement) {
    return existingElement
  }

  return await new Promise<HTMLElement | null>((resolve) => {
    let settled = false
    let observer: MutationObserver
    let timer = 0
    const finish = (element: HTMLElement | null) => {
      if (settled) {
        return
      }
      settled = true
      observer.disconnect()
      window.clearTimeout(timer)
      resolve(element)
    }
    const check = () => {
      const element = findElement()
      if (element) {
        finish(element)
      }
    }
    observer = new MutationObserver(check)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
      childList: true,
      subtree: true,
    })
    timer = window.setTimeout(() => finish(findElement()), timeout)
    window.requestAnimationFrame(check)
  })
}

function rememberGuideSurface(element: HTMLElement | null) {
  if (!element) {
    return
  }
  const selector = businessSurfaceSelectors.join(',')
  const surface = element.matches(selector) ? element : element.closest<HTMLElement>(selector)
  if (surface) {
    guideDetailSurfaces.add(surface)
  }
}

async function waitForSurfaceToClose(surface: HTMLElement, timeout = 500) {
  const started = Date.now()
  while (isElementVisible(surface) && Date.now() - started < timeout) {
    await new Promise(resolve => window.setTimeout(resolve, 40))
  }
}

async function closeGuideDetailSurfaces() {
  const surfaces = [...guideDetailSurfaces].reverse()
  for (const surface of surfaces) {
    if (!isElementVisible(surface)) {
      guideDetailSurfaces.delete(surface)
      continue
    }
    const closeButton = surface.querySelector<HTMLElement>([
      '[data-guide-close]',
      '.el-dialog__headerbtn',
      '.el-drawer__close-btn',
      '.cr-drawer-close',
      '[aria-label="Close"]',
      '[aria-label="close"]',
      '[aria-label="关闭"]',
    ].join(','))
    closeButton?.click()
    if (closeButton) {
      await waitForSurfaceToClose(surface)
    }
    guideDetailSurfaces.delete(surface)
  }
  if (guideDetailReturnRoute) {
    const returnRoute = guideDetailReturnRoute
    guideDetailReturnRoute = ''
    try {
      guideNavigationTarget = router.resolve(returnRoute).path
      await router.push(returnRoute)
    }
    finally {
      guideNavigationTarget = ''
    }
  }
}

async function openCooperationDetailSurface(step: GuideStep, timeout: number) {
  const before = step.before
  if (!before?.click || !before.waitFor) {
    return null
  }

  const startedAt = Date.now()
  while (Date.now() - startedAt < timeout) {
    const surface = findVisibleElement([before.waitFor])
    if (surface) {
      rememberGuideSurface(surface)
      return surface
    }

    const trigger = findVisibleElement([before.click])
    if (trigger) {
      trigger.click()
      await nextTick()
      await new Promise(resolve => window.setTimeout(resolve, 260))
    }
    else {
      await new Promise(resolve => window.setTimeout(resolve, 180))
    }
  }
  return findVisibleElement([before.waitFor])
}

async function runBeforeAction(step: GuideStep) {
  const before = step.before
  if (!before) {
    return
  }

  for (const surfaceSelector of before.close || []) {
    const surface = findVisibleElement([surfaceSelector])
    const closeButton = surface?.querySelector<HTMLElement>(
      '[data-guide-close], .el-dialog__headerbtn, .el-drawer__close-btn, .cr-drawer-close, [aria-label="Close"], [aria-label="close"], [aria-label="关闭"]',
    )
    closeButton?.click()
    if (surface && closeButton) {
      await waitForSurfaceToClose(surface, 1400)
    }
    if (surface) {
      guideDetailSurfaces.delete(surface)
    }
  }

  let routeBeforeOpen = ''
  let openedSurface = false
  let skipDeclaredWait = false
  const stepTimeout = step.id.startsWith('cooperation-')
    ? 8000
    : step.id.startsWith('config-advanced-')
      ? 12000
      : step.id.startsWith('org-')
        ? 9000
        : step.id.startsWith('config-') ? 8000 : 3200

  if (before.click && before.safeAction === 'open-surface') {
    routeBeforeOpen = route.fullPath
    const expectedSurface = before.waitFor ? findVisibleElement([before.waitFor]) : null
    openedSurface = Boolean(expectedSurface)
    rememberGuideSurface(expectedSurface)

    if (!openedSurface && /^cooperation-(?:supplier|customer)-detail$/.test(step.id)) {
      const detailSurface = await openCooperationDetailSurface(step, stepTimeout)
      openedSurface = Boolean(detailSurface)
      skipDeclaredWait = !openedSurface
    }
    else if (!openedSurface) {
      const clickTarget = await waitForElement(
        () => findVisibleElement([before.click || '']),
        stepTimeout,
      )
      if (clickTarget) {
        clickTarget.click()
        openedSurface = true
      }
    }
    await nextTick()
  }

  // 没有打开弹层动作时，页签必须先切换，再等待页签内部的目标出现。
  // 例如系统设置的供应商、站点配置，旧顺序会先等待隐藏内容，导致业务流程中断。
  if (!before.click && before.tab) {
    await activateGuideTab(before.tab, stepTimeout)
  }

  if (before.waitFor && !skipDeclaredWait) {
    // 只接受当前步骤声明的目标，不能用任意 dialog/drawer 代替，
    // 否则角色菜单、权限设置会错误定位到上一个弹层。
    const surfaceTarget = await waitForElement(
      () => findVisibleElement([before.waitFor || '']),
      stepTimeout,
    )
    rememberGuideSurface(surfaceTarget)
    if (routeBeforeOpen && route.fullPath !== routeBeforeOpen) {
      guideDetailReturnRoute = routeBeforeOpen
    }
  }

  // 打开设计器等弹层后，页签位于弹层内部，需要在弹层完成渲染后切换。
  if (before.click && before.tab) {
    await activateGuideTab(before.tab, stepTimeout)
  }

  if (openedSurface && routeBeforeOpen && route.fullPath !== routeBeforeOpen) {
    guideDetailReturnRoute = routeBeforeOpen
  }
}

/**
 * 返回当前步骤真正需要圈选的业务矩形。
 *
 * 供应商网络运营版的“创建项目与发布前检查”第一步需要讲解项目表格，
 * 高亮顶部必须从 Element Plus 表头开始，而不是从表格外层容器的留白开始。
 * 这里仍使用项目表格本身的左右边界和底部，因此固定列、操作列与横向滚动区
 * 都保持在同一个高亮范围内。其他版本、其他步骤继续使用原目标矩形。
 */
function resolveGuideTargetRect(element: HTMLElement) {
  const step = onboardingStore.currentStep
  if (
    onboardingStore.edition.id === 'supplier-network'
    && step?.id === 'project-table'
  ) {
    const table = element.matches('.el-table')
      ? element
      : element.querySelector<HTMLElement>('.el-table')
    const header = table?.querySelector<HTMLElement>('.el-table__header-wrapper')
    if (table && header && isElementVisible(header)) {
      const tableRect = table.getBoundingClientRect()
      const headerRect = header.getBoundingClientRect()
      // highlightBounds 会在四周扩展 6px；这里反向收缩，确保最终边框
      // 精确从表头顶部开始，并贴合整张表格的左右与底部边界。
      return new DOMRect(
        tableRect.left + 6,
        headerRect.top + 6,
        Math.max(20, tableRect.width - 12),
        Math.max(20, tableRect.bottom - headerRect.top - 12),
      )
    }
  }
  return element.getBoundingClientRect()
}

function updateTargetRect() {
  if (targetElement.value?.isConnected) {
    targetRect.value = resolveGuideTargetRect(targetElement.value)
  }
}

function isSameGuideRect(left: DOMRect, right: DOMRect, tolerance = 0.5) {
  return Math.abs(left.left - right.left) <= tolerance
    && Math.abs(left.top - right.top) <= tolerance
    && Math.abs(left.width - right.width) <= tolerance
    && Math.abs(left.height - right.height) <= tolerance
}

async function waitForStableTargetRect(
  element: HTMLElement,
  timeout = 1200,
  stableFrames = 3,
) {
  const startedAt = performance.now()
  let previousRect: DOMRect | null = null
  let stableCount = 0
  let currentRect = resolveGuideTargetRect(element)

  while (performance.now() - startedAt < timeout) {
    await new Promise<void>(resolve => window.requestAnimationFrame(() => resolve()))
    if (!element.isConnected) {
      return currentRect
    }

    currentRect = resolveGuideTargetRect(element)
    if (previousRect && isSameGuideRect(previousRect, currentRect)) {
      stableCount += 1
      if (stableCount >= stableFrames) {
        return currentRect
      }
    }
    else {
      stableCount = 0
    }
    previousRect = currentRect
  }

  return currentRect
}

function isAnimatedGuideSurfaceTarget(element: HTMLElement) {
  return Boolean(element.closest('.el-overlay, .el-dialog, .el-drawer, .cr-drawer, .coop-detail-drawer, [role="dialog"]'))
}

function shouldWaitForStableGuideTarget(element: HTMLElement, step: GuideStep) {
  return isAnimatedGuideSurfaceTarget(element)
    || step.id === 'config-integration-credentials'
    || step.id === 'config-integration-test'
}

function observeGuideTargetLayout(element: HTMLElement) {
  targetResizeObserver?.observe(element)

  // 异步字段渲染时，按钮自身尺寸可能不变，但父布局会把按钮位置向下推。
  // 同时观察最近的布局容器，容器高度变化后立即重新计算高亮区域。
  const layoutContainer = element.closest<HTMLElement>(
    '.el-form, .main-container, .el-dialog__body, .el-drawer__body',
  )
  if (layoutContainer && layoutContainer !== element) {
    targetResizeObserver?.observe(layoutContainer)
  }
}

function normalizeGuidePath(value: unknown) {
  const normalized = String(value || '')
    .split(/[?#]/)[0]
    .replace(/\\/g, '/')
    .replace(/\/{2,}/g, '/')
  if (!normalized) {
    return ''
  }
  return normalized.length > 1 ? normalized.replace(/\/$/, '') : normalized
}

function currentRouteMatches(record: BackendGuideRouteRecord) {
  const current = router.currentRoute.value
  if (record.name && String(current.name || '') === record.name) {
    return true
  }
  return normalizeGuidePath(current.path) === normalizeGuidePath(record.fullPath)
}

async function waitForGuideRouteRecord(
  step: GuideStep,
  timeout = 30000,
): Promise<GuideRuntimeResult<BackendGuideRouteRecord>> {
  const startedAt = Date.now()
  while (Date.now() - startedAt < timeout) {
    if (routeStore.backendGuideRoutesStatus === 'error') {
      return guideRuntimeFailure('route-status-error', step.pageKey)
    }

    // 必须等 routeList 请求、动态路由转换和 router.addRoute 全部完成。
    if (routeStore.backendGuideRoutesStatus !== 'ready') {
      await new Promise(resolve => window.setTimeout(resolve, 120))
      continue
    }

    const resolution = resolveGuideRouteRecordDetailed(
      routeStore.backendGuideRoutes,
      step.pageKey,
      onboardingStore.edition.id,
    )
    if (resolution.status === 'not-found') {
      return guideRuntimeFailure('route-not-found', step.pageKey)
    }
    if (resolution.status === 'ambiguous') {
      return guideRuntimeFailure(
        'route-ambiguous',
        `${step.pageKey}: ${resolution.candidates.map(item => item.record.fullPath).join(', ')}`,
      )
    }
    if (!resolution.record) {
      return guideRuntimeFailure('route-not-found', step.pageKey)
    }
    if (!routeStore.isBackendGuideRouteRegistered(resolution.record)) {
      return guideRuntimeFailure('route-not-registered', resolution.record.fullPath)
    }
    return guideRuntimeSuccess(resolution.record)
  }
  return guideRuntimeFailure('route-ready-timeout', step.pageKey)
}

async function navigateToGuideRoute(
  record: BackendGuideRouteRecord,
  step: GuideStep,
): Promise<GuideRuntimeResult<HTMLElement>> {
  if (!routeStore.isBackendGuideRouteRegistered(record)) {
    return guideRuntimeFailure('route-not-registered', record.fullPath)
  }

  const destination = record.name && router.hasRoute(record.name)
    ? { name: record.name }
    : record.fullPath
  const resolved = router.resolve(destination)
  if (!resolved.matched.length) {
    return guideRuntimeFailure('router-resolve-failed', record.fullPath)
  }

  const previousRoutePath = router.currentRoute.value.fullPath
  const previousPageRoot = currentBusinessPageRoot()
  guideNavigationTarget = record.fullPath
  try {
    try {
      await router.push(destination)
    }
    catch (error) {
      return guideRuntimeFailure(
        'navigation-error',
        error instanceof Error ? error.message : String(error),
      )
    }

    const routeMatched = await waitForElement(
      () => currentRouteMatches(record) ? document.documentElement : null,
      10000,
    )
    if (!routeMatched) {
      return guideRuntimeFailure('route-change-timeout', record.fullPath)
    }

    const routeChanged = normalizeGuidePath(previousRoutePath)
      !== normalizeGuidePath(router.currentRoute.value.fullPath)
    const mountedResult = await waitForGuidePageMounted({
      step,
      routeMatched: () => currentRouteMatches(record),
      getFallbackRoot: currentBusinessPageRoot,
      previousRoot: previousPageRoot,
      acceptCurrentRoot: !routeChanged,
      timeout: 15000,
    })
    if (isGuideRuntimeFailure(mountedResult)) {
      return mountedResult
    }
    return guideRuntimeSuccess(mountedResult.value.root)
  }
  finally {
    guideNavigationTarget = ''
  }
}

function findScrollableAncestor(element: HTMLElement) {
  let current = element.parentElement
  while (current) {
    const style = window.getComputedStyle(current)
    if (/auto|scroll/.test(`${style.overflowY} ${style.overflow}`) && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null
}

function resetGuideScrollPosition(element: HTMLElement) {
  const resetElements = new Set<HTMLElement>()
  const resetElement = (scrollElement: HTMLElement) => {
    if (resetElements.has(scrollElement)) {
      return
    }
    resetElements.add(scrollElement)
    scrollElement.scrollTop = 0
    scrollElement.scrollLeft = 0
  }

  // 目标本身可能是抽屉 body 或表格滚动区。
  resetElement(element)
  element.querySelectorAll<HTMLElement>([
    '.el-scrollbar__wrap',
    '.el-table__body-wrapper',
    '.el-table__body-wrapper .el-scrollbar__wrap',
  ].join(', ')).forEach(resetElement)

  // 页面、抽屉和弹窗会分别维护自己的滚动位置，切换步骤时必须全部回到起点。
  let current = element.parentElement
  while (current) {
    const style = window.getComputedStyle(current)
    const canScrollVertically = /auto|scroll/.test(`${style.overflowY} ${style.overflow}`)
      && current.scrollHeight > current.clientHeight
    const canScrollHorizontally = /auto|scroll/.test(`${style.overflowX} ${style.overflow}`)
      && current.scrollWidth > current.clientWidth
    if (canScrollVertically || canScrollHorizontally) {
      resetElement(current)
    }
    current = current.parentElement
  }

  const documentScroller = document.scrollingElement
  if (documentScroller instanceof HTMLElement) {
    resetElement(documentScroller)
  }
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
}

function getGuideVisibleBounds(element: HTMLElement) {
  const margin = 10
  const mainViewport = document.querySelector<HTMLElement>('#app-main .main-container .main')
  const surfaceBody = element.matches('.el-drawer__body, .el-dialog__body')
    ? element
    : element.closest<HTMLElement>('.el-drawer__body, .el-dialog__body')
  const viewportElement = surfaceBody
    || (mainViewport?.contains(element) ? mainViewport : null)
  const viewportRect = viewportElement?.getBoundingClientRect()
  return {
    top: Math.max(margin, (viewportRect?.top || 0) + margin),
    bottom: Math.min(window.innerHeight - margin, (viewportRect?.bottom || window.innerHeight) - margin),
  }
}

/**
 * 所有教程步骤统一通过这里处理目标位置，避免页面、表格、抽屉各自保留的滚动状态
 * 造成高亮区域被顶部页签或视口底部截断。
 */
async function positionGuideTargetInViewport(
  element: HTMLElement,
  options: { isTable?: boolean, keepPageTop?: boolean } = {},
) {
  resetGuideScrollPosition(element)
  await nextTick()

  if (!options.keepPageTop) {
    const bounds = getGuideVisibleBounds(element)
    const rect = element.getBoundingClientRect()
    const availableHeight = Math.max(1, bounds.bottom - bounds.top)
    const isLargeTarget = options.isTable || rect.height >= availableHeight * 0.72
    element.scrollIntoView({
      behavior: 'auto',
      block: isLargeTarget ? 'start' : 'center',
      inline: 'nearest',
    })
    await nextTick()

    const alignedBounds = getGuideVisibleBounds(element)
    const alignedRect = element.getBoundingClientRect()
    const alignedAvailableHeight = Math.max(1, alignedBounds.bottom - alignedBounds.top)
    let delta = 0
    if (alignedRect.height >= alignedAvailableHeight) {
      delta = alignedRect.top - alignedBounds.top
    }
    else if (alignedRect.top < alignedBounds.top) {
      delta = alignedRect.top - alignedBounds.top
    }
    else if (alignedRect.bottom > alignedBounds.bottom) {
      delta = alignedRect.bottom - alignedBounds.bottom
    }

    if (Math.abs(delta) >= 1) {
      const scrollContainer = findScrollableAncestor(element)
      if (scrollContainer && scrollContainer !== document.documentElement && scrollContainer !== document.body) {
        scrollContainer.scrollBy({ top: delta, left: 0, behavior: 'auto' })
      }
      else {
        window.scrollBy({ top: delta, left: 0, behavior: 'auto' })
      }
      await nextTick()
    }
  }

  // Element Plus 布局会在滚动后异步同步表头和滚动条，等待一帧再读取最终矩形。
  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
}

async function revealLatestSampleFinanceTarget(step: GuideStep) {
  if (
    onboardingStore.edition.id !== 'sample-library'
    || step.id !== 'finance-bill-risk'
    || hasDataConstraint.value
  ) {
    return
  }
  const toolbar = await waitForElement(
    () => findVisibleElement(step.selectors, currentBusinessPageRoot() || document),
    12000,
  )
  if (!toolbar) {
    return
  }

  // 对齐最新版样本库教程第 4 步：操作区不得被最上方导航遮挡。
  const topbar = findVisibleElement(['.topbar-container', 'header'])
  const safeTop = Math.max(16, (topbar?.getBoundingClientRect().bottom || 0) + 16)
  const toolbarRect = toolbar.getBoundingClientRect()
  if (toolbarRect.top < safeTop) {
    const scrollContainer = findScrollableAncestor(toolbar)
    const delta = toolbarRect.top - safeTop
    if (scrollContainer && scrollContainer !== document.documentElement && scrollContainer !== document.body) {
      scrollContainer.scrollBy({ top: delta, left: 0, behavior: 'auto' })
    }
    else {
      window.scrollBy({ top: delta, left: 0, behavior: 'auto' })
    }
  }
  else if (toolbarRect.bottom > window.innerHeight - 16) {
    toolbar.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' })
  }
  await nextTick()
}

function observeDeferredBusinessData(
  step: GuideStep,
  mountedPage: HTMLElement,
  rowSelector?: string,
  requiredSelector?: string,
) {
  dataStateMutationObserver?.disconnect()
  dataStateMutationObserver = null
  const dataSelector = requiredSelector || rowSelector
  if (!dataSelector || !isDeferredAvailability(dataAvailability.value)) {
    return
  }

  dataStateMutationObserver = new MutationObserver(() => {
    if (
      onboardingStore.status !== 'running'
      || onboardingStore.currentStep?.id !== step.id
      || !findVisibleElement([dataSelector], mountedPage)
    ) {
      return
    }
    dataStateMutationObserver?.disconnect()
    dataStateMutationObserver = null
    void prepareStep()
  })
  dataStateMutationObserver.observe(mountedPage, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'hidden', 'style'],
  })
}

function runtimeErrorTranslationKey(code: GuideRuntimeFailure['code']) {
  return code.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

const runtimeErrorBadge = computed(() => runtimeError.value
  ? t(`onboarding.runtimeErrors.${runtimeErrorTranslationKey(runtimeError.value.code)}.badge`)
  : t('onboarding.targetMissing.badge'))
const runtimeErrorTitle = computed(() => runtimeError.value
  ? t(`onboarding.runtimeErrors.${runtimeErrorTranslationKey(runtimeError.value.code)}.title`)
  : t('onboarding.targetMissing.title'))
const runtimeErrorDescription = computed(() => runtimeError.value
  ? t(`onboarding.runtimeErrors.${runtimeErrorTranslationKey(runtimeError.value.code)}.description`)
  : t('onboarding.targetMissing.description'))

function showRuntimeError(error: GuideRuntimeFailure) {
  runtimeError.value = error
  console.warn('[OnboardingGuide] runtime failure', {
    code: error.code,
    detail: error.detail,
    editionId: onboardingStore.edition.id,
    taskId: onboardingStore.currentTask?.id,
    stepId: onboardingStore.currentStep?.id,
    pageKey: onboardingStore.currentStep?.pageKey,
    route: router.currentRoute.value.fullPath,
  })
  onboardingStore.setTargetMissing(true)
  ready.value = true
}

function clearRuntimeError() {
  runtimeError.value = null
  onboardingStore.setTargetMissing(false)
}

function handleRetryRuntime() {
  runtimeError.value = null
  onboardingStore.retryTarget()
}

async function prepareStep() {
  const generation = ++prepareGeneration
  dataStateMutationObserver?.disconnect()
  dataStateMutationObserver = null
  ready.value = false
  runtimeError.value = null
  dataAvailability.value = { status: 'ready', targetCandidates: [] }
  targetRect.value = null
  targetResizeObserver?.disconnect()
  targetElement.value?.classList.remove('onboarding-guide-target')
  targetElement.value = null

  const step = onboardingStore.currentStep
  if (onboardingStore.status !== 'running' || !step) {
    return
  }

  // “调查站运营闭环”的第一步必须先切换主导航中的调查站模块。
  // 不能仅给当前侧栏添加 CSS 类，否则页面仍停留在其他业务菜单。
  if (step.id === 'station-nav') {
    await activateSurveyStationMenu()
    if (generation !== prepareGeneration) {
      return
    }
  }

  const routeTimeout = step.id.startsWith('finance-') || step.id.startsWith('org-')
    ? 30000
    : step.id.startsWith('cooperation-') ? 20000 : 15000
  const routeResult = step.routeMode === 'workspace'
    ? null
    : await waitForGuideRouteRecord(step, routeTimeout)

  if (routeResult && isGuideRuntimeFailure(routeResult)) {
    // 仅当 routeList 已完成且明确没有返回该业务页面时自动跳过。
    // ambiguous、注册失败、接口失败仍显示精确错误，避免误跳真实配置问题。
    if (routeResult.code === 'route-not-found') {
      onboardingStore.excludeCurrentStepForUnavailableRoute()
      ready.value = false
      return
    }
    showRuntimeError(routeResult)
    return
  }

  const targetRecord = routeResult?.ok ? routeResult.value : null
  let mountedPage: HTMLElement | null = null
  const currentDetailTarget = step.surface === 'detail'
    ? findVisibleElement([...step.selectors, step.before?.waitFor || ''].filter(Boolean))
    : null
  if (targetRecord && !currentRouteMatches(targetRecord) && !currentDetailTarget) {
    const navigationResult = await navigateToGuideRoute(targetRecord, step)
    if (isGuideRuntimeFailure(navigationResult)) {
      showRuntimeError(navigationResult)
      return
    }
    mountedPage = navigationResult.value
    if (generation !== prepareGeneration) {
      return
    }
  }

  await nextTick()
  if (!mountedPage) {
    const mountedResult = await waitForGuidePageMounted({
      step,
      routeMatched: () => !targetRecord || currentRouteMatches(targetRecord),
      getFallbackRoot: currentBusinessPageRoot,
      acceptCurrentRoot: true,
      timeout: 15000,
    })
    if (isGuideRuntimeFailure(mountedResult)) {
      showRuntimeError(mountedResult)
      return
    }
    mountedPage = mountedResult.value.root
  }
  decorateRuntimeStepTargets(step)
  if (!onboardingStore.currentTask) {
    return
  }
  const guideDataRule = getGuideDataRule(onboardingStore.edition.id, step.id)
  const hasDeclaredBusinessData = Boolean(
    (guideDataRule?.requiredSelector
      && findVisibleElement([guideDataRule.requiredSelector]))
    || (guideDataRule?.rowSelector
      && findVisibleElement([guideDataRule.rowSelector])),
  )
  if (guideDataRule?.emptyBefore && !hasDeclaredBusinessData) {
    runningBeforeAction = true
    try {
      await runBeforeAction({ ...step, before: guideDataRule.emptyBefore })
    }
    finally {
      runningBeforeAction = false
    }
  }
  let availability: GuideDataAvailability = { status: 'ready', targetCandidates: [] }
  if (guideDataRule) {
    while (true) {
      availability = await evaluateGuideStepData(
        step,
        mountedPage,
        onboardingStore.edition.id,
      )
      if (availability.status !== 'loading') {
        break
      }
      await new Promise(resolve => window.setTimeout(resolve, 180))
      if (
        generation !== prepareGeneration
        || onboardingStore.status !== 'running'
        || onboardingStore.currentStep?.id !== step.id
      ) {
        return
      }
    }
  }
  if (
    generation !== prepareGeneration
    || onboardingStore.status !== 'running'
    || onboardingStore.currentStep?.id !== step.id
  ) {
    return
  }
  dataAvailability.value = availability
  onboardingStore.setStepDataStatus(step.id, dataAvailability.value.status)
  observeDeferredBusinessData(
    step,
    mountedPage,
    guideDataRule?.rowSelector,
    guideDataRule?.requiredSelector,
  )
  runningBeforeAction = true
  try {
    if (!hasDataConstraint.value) {
      await runBeforeAction(step)
    }
  }
  finally {
    runningBeforeAction = false
  }
  // 最新样本库财务第 4 步要求在页面与 before 行为完成后再显露工具栏。
  await revealLatestSampleFinanceTarget(step)
  if (generation !== prepareGeneration || onboardingStore.currentStep?.id !== step.id) {
    return
  }
  const matchedElement = await waitForElement(
    findTarget,
    hasDataConstraint.value
      ? 2200
      : step.id.startsWith('cooperation-')
        ? 12000
        : step.id.startsWith('config-advanced-')
          ? 15000
          : step.id.startsWith('finance-') || step.id.startsWith('org-')
            ? 15000
            : step.id.startsWith('customer-') || step.id.startsWith('project-') ? 9000 : 6000,
  )

  if (!matchedElement) {
    showRuntimeError(guideRuntimeFailure('target-not-found', `${step.id}: ${step.selectors.join(', ')}`))
    return
  }

  const element = resolveSurfaceContentTarget(matchedElement)
  clearRuntimeError()
  targetElement.value = element
  element.classList.add('onboarding-guide-target')
  const isTableTarget = step.target === 'table'
  const shouldResetEmployeeScroll = step.id === 'org-employee-table'
  await positionGuideTargetInViewport(element, {
    isTable: isTableTarget,
    keepPageTop: shouldResetEmployeeScroll,
  })
  await new Promise(resolve => window.setTimeout(resolve, 40))
  if (generation !== prepareGeneration) {
    return
  }

  if (shouldWaitForStableGuideTarget(element, step)) {
    targetRect.value = await waitForStableTargetRect(element)
  }
  else {
    updateTargetRect()
  }
  if (generation !== prepareGeneration) {
    return
  }

  observeGuideTargetLayout(element)
  ready.value = true
}

const highlightBounds = computed(() => {
  const rect = targetRect.value
  if (!rect || onboardingStore.targetMissing) {
    return null
  }
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const mainViewport = document.querySelector<HTMLElement>('#app-main .main-container .main')
  const isOverlayTarget = Boolean(
    targetElement.value?.closest('.el-overlay, .el-drawer, .el-dialog, [role="dialog"]'),
  )
  const shouldClipToMain = Boolean(
    mainViewport
    && targetElement.value
    && mainViewport.contains(targetElement.value)
    && !isOverlayTarget,
  )
  const mainRect = shouldClipToMain ? mainViewport?.getBoundingClientRect() : null
  const topbar = shouldClipToMain
    ? document.querySelector<HTMLElement>('.topbar-container')
    : null
  const topbarRect = topbar && isElementVisible(topbar)
    ? topbar.getBoundingClientRect()
    : null
  const clipLeft = Math.max(4, mainRect?.left || 4)
  const clipTop = Math.max(4, mainRect?.top || 4, topbarRect?.bottom || 4)
  const clipRight = Math.min(viewportWidth - 4, mainRect?.right || viewportWidth - 4)
  const clipBottom = Math.min(viewportHeight - 4, mainRect?.bottom || viewportHeight - 4)
  const left = Math.max(clipLeft, Math.min(clipRight, rect.left - 6))
  const top = Math.max(clipTop, Math.min(clipBottom, rect.top - 6))
  const right = Math.max(left + 20, Math.min(clipRight, rect.right + 6))
  const bottom = Math.max(top + 20, Math.min(clipBottom, rect.bottom + 6))
  return { left, top, right, bottom }
})

const highlightStyle = computed(() => {
  const bounds = highlightBounds.value
  if (!bounds) {
    return { display: 'none' }
  }
  return {
    left: `${bounds.left}px`,
    top: `${bounds.top}px`,
    width: `${bounds.right - bounds.left}px`,
    height: `${bounds.bottom - bounds.top}px`,
  }
})

const maskStyles = computed(() => {
  const bounds = highlightBounds.value
  if (!bounds) {
    return [{ inset: '0' }]
  }
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  return [
    { left: '0', top: '0', width: `${viewportWidth}px`, height: `${bounds.top}px` },
    { left: '0', top: `${bounds.top}px`, width: `${bounds.left}px`, height: `${bounds.bottom - bounds.top}px` },
    {
      left: `${bounds.right}px`,
      top: `${bounds.top}px`,
      width: `${Math.max(0, viewportWidth - bounds.right)}px`,
      height: `${bounds.bottom - bounds.top}px`,
    },
    {
      left: '0',
      top: `${bounds.bottom}px`,
      width: `${viewportWidth}px`,
      height: `${Math.max(0, viewportHeight - bounds.bottom)}px`,
    },
  ]
})

function clampCardPosition(value: number, minimum: number, maximum: number) {
  return Math.max(minimum, Math.min(Math.max(minimum, maximum), value))
}

const cardLayout = computed(() => {
  const rect = targetRect.value
  const viewport = viewportBounds.value
  const margin = 12
  const gap = 16
  const safeLeft = viewport.left + margin
  const safeTop = viewport.top + margin
  const safeRight = viewport.left + viewport.width - margin
  const safeBottom = viewport.top + viewport.height - margin
  const desiredWidth = onboardingStore.targetMissing ? 440 : 380
  const width = Math.max(1, Math.min(desiredWidth, viewport.width - margin * 2))
  const fallbackHeight = onboardingStore.targetMissing ? 300 : hasDataConstraint.value ? 360 : 280
  const height = cardSize.value.height || fallbackHeight
  const maxLeft = safeRight - width
  const maxTop = safeBottom - height
  const centeredLeft = clampCardPosition(
    viewport.left + (viewport.width - width) / 2,
    safeLeft,
    maxLeft,
  )
  const centeredTop = clampCardPosition(
    viewport.top + (viewport.height - height) / 2,
    safeTop,
    maxTop,
  )
  const baseStyle = {
    width: `${width}px`,
  }

  if (!rect || onboardingStore.targetMissing) {
    return {
      placement: 'center',
      style: {
        ...baseStyle,
        left: `${centeredLeft}px`,
        top: `${centeredTop}px`,
      },
    }
  }

  if (viewport.width <= 767) {
    return {
      placement: 'center',
      style: {
        ...baseStyle,
        left: `${safeLeft}px`,
        top: `${maxTop}px`,
      },
    }
  }

  if (rect.right + gap + width <= safeRight) {
    return {
      placement: 'right',
      style: {
        ...baseStyle,
        left: `${rect.right + gap}px`,
        top: `${clampCardPosition(rect.top, safeTop, maxTop)}px`,
      },
    }
  }

  if (rect.left - gap - width >= safeLeft) {
    return {
      placement: 'left',
      style: {
        ...baseStyle,
        left: `${rect.left - gap - width}px`,
        top: `${clampCardPosition(rect.top, safeTop, maxTop)}px`,
      },
    }
  }

  if (rect.bottom + gap + height <= safeBottom) {
    return {
      placement: 'bottom',
      style: {
        ...baseStyle,
        left: `${clampCardPosition(rect.left, safeLeft, maxLeft)}px`,
        top: `${rect.bottom + gap}px`,
      },
    }
  }

  if (rect.top - gap - height >= safeTop) {
    return {
      placement: 'top',
      style: {
        ...baseStyle,
        left: `${clampCardPosition(rect.left, safeLeft, maxLeft)}px`,
        top: `${rect.top - gap - height}px`,
      },
    }
  }

  const targetCenterX = rect.left + rect.width / 2
  const targetCenterY = rect.top + rect.height / 2
  const viewportCenterX = viewport.left + viewport.width / 2
  const viewportCenterY = viewport.top + viewport.height / 2
  return {
    placement: 'center',
    style: {
      ...baseStyle,
      left: `${targetCenterX >= viewportCenterX ? safeLeft : maxLeft}px`,
      top: `${targetCenterY <= viewportCenterY ? maxTop : safeTop}px`,
    },
  }
})

const cardStyle = computed(() => cardLayout.value.style)
const cardPlacement = computed(() => cardLayout.value.placement)

function updateViewportBounds() {
  const viewport = window.visualViewport
  viewportBounds.value = {
    left: viewport?.offsetLeft || 0,
    top: viewport?.offsetTop || 0,
    width: viewport?.width || window.innerWidth,
    height: viewport?.height || window.innerHeight,
  }
}

function measureCard() {
  const card = cardElement.value
  if (!card) {
    cardSize.value = { width: 0, height: 0 }
    return
  }
  const rect = card.getBoundingClientRect()
  const nextSize = {
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  }
  if (nextSize.width !== cardSize.value.width || nextSize.height !== cardSize.value.height) {
    cardSize.value = nextSize
  }
}

function observeCard(element: HTMLElement | null) {
  cardResizeObserver?.disconnect()
  if (!element) {
    cardSize.value = { width: 0, height: 0 }
    return
  }
  cardResizeObserver?.observe(element)
  window.requestAnimationFrame(measureCard)
}

function handleViewportChange() {
  updateViewportBounds()
  updateTargetRect()
  measureCard()
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && onboardingStore.status === 'running') {
    void handlePause()
  }
}

function shouldCloseDetailBefore(nextStep: GuideStep | undefined) {
  const currentStep = onboardingStore.currentStep
  if (!currentStep || (guideDetailSurfaces.size === 0 && !guideDetailReturnRoute)) {
    return false
  }
  if (nextStep?.before?.close?.length) {
    return true
  }
  return !nextStep
    || nextStep.pageKey !== currentStep.pageKey
    || nextStep.surface !== 'detail'
}

async function handleNext() {
  if (progressSubmitting.value) {
    return
  }
  progressSubmitting.value = true
  const nextStep = onboardingStore.currentSteps[onboardingStore.activeStepIndex + 1]
  try {
    if (shouldCloseDetailBefore(nextStep)) {
      try {
        await closeGuideDetailSurfaces()
      }
      catch (error) {
        // 关闭业务弹层或返回原路由失败时，仍然保存用户已经确认的教程步骤。
        console.warn('[OnboardingGuide] 关闭步骤关联页面失败，继续保存教程进度。', error)
      }
    }
    await onboardingStore.next({ deferred: isDeferredAvailability(dataAvailability.value) })
  }
  finally {
    progressSubmitting.value = false
  }
}

async function handleSkipStep() {
  const nextStep = onboardingStore.currentSteps[onboardingStore.activeStepIndex + 1]
  if (shouldCloseDetailBefore(nextStep)) {
    await closeGuideDetailSurfaces()
  }
  onboardingStore.skipStep()
}

async function handlePrevious() {
  const previousStep = onboardingStore.currentSteps[onboardingStore.activeStepIndex - 1]
  if (shouldCloseDetailBefore(previousStep)) {
    await closeGuideDetailSurfaces()
  }
  onboardingStore.previous()
}

async function handlePause() {
  await closeGuideDetailSurfaces()
  onboardingStore.pause()
}

watch(
  () => [
    onboardingStore.status,
    onboardingStore.activeTaskId,
    onboardingStore.activeStepIndex,
    onboardingStore.currentStep?.id,
    onboardingStore.targetMissing,
  ],
  ([status, _taskId, _stepIndex, _stepId, missing], previous) => {
    if (status === 'running' && (!missing || previous?.[4] === true)) {
      prepareStep()
    }
  },
  { immediate: true },
)

watch(() => route.fullPath, () => {
  if (
    onboardingStore.status === 'running'
    && !runningBeforeAction
    && route.path !== guideNavigationTarget
  ) {
    prepareStep()
  }
})

watch(cardElement, element => observeCard(element), { flush: 'post' })

onMounted(() => {
  cardResizeObserver = new ResizeObserver(measureCard)
  targetResizeObserver = new ResizeObserver(() => {
    window.requestAnimationFrame(updateTargetRect)
  })
  observeCard(cardElement.value)
  updateViewportBounds()
  window.addEventListener('resize', handleViewportChange)
  window.addEventListener('scroll', updateTargetRect, true)
  window.addEventListener('keydown', handleKeydown)
  window.visualViewport?.addEventListener('resize', handleViewportChange)
  window.visualViewport?.addEventListener('scroll', handleViewportChange)
})

onBeforeUnmount(() => {
  prepareGeneration += 1
  cardResizeObserver?.disconnect()
  targetResizeObserver?.disconnect()
  dataStateMutationObserver?.disconnect()
  targetElement.value?.classList.remove('onboarding-guide-target')
  window.removeEventListener('resize', handleViewportChange)
  window.removeEventListener('scroll', updateTargetRect, true)
  window.removeEventListener('keydown', handleKeydown)
  window.visualViewport?.removeEventListener('resize', handleViewportChange)
  window.visualViewport?.removeEventListener('scroll', handleViewportChange)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="onboardingStore.status === 'running' && onboardingStore.currentStep && ready"
      class="onboarding-tour"
      :class="{
        'is-simulate': !hasDataConstraint && onboardingStore.currentStep.interaction === 'simulate',
        'is-data-state': hasDataConstraint,
      }"
      role="dialog"
      aria-modal="true"
      :aria-label="stepTitle"
    >
      <div
        v-for="(maskStyle, maskIndex) in maskStyles"
        :key="maskIndex"
        class="onboarding-tour__mask"
        :style="maskStyle"
      />
      <div class="onboarding-tour__highlight" :style="highlightStyle" />
      <section
        ref="cardElement"
        class="onboarding-tour__card"
        :class="{ 'is-missing': onboardingStore.targetMissing }"
        :data-placement="cardPlacement"
        :style="cardStyle"
      >
        <header v-if="!onboardingStore.targetMissing" class="onboarding-tour__head">
          <span>
            {{ taskTitle }}
            · {{ t('onboarding.setup.stepLabel', {
              current: onboardingStore.activeStepIndex + 1,
              total: onboardingStore.currentSteps.length,
            }) }}
          </span>
          <el-button
            class="onboarding-tour__close"
            :aria-label="t('onboarding.common.pause')"
            :disabled="progressBusy"
            @click="handlePause"
          >
            <FaIcon name="i-ph:x" />
          </el-button>
        </header>

        <template v-if="onboardingStore.targetMissing">
          <span class="onboarding-tour__readonly">{{ runtimeErrorBadge }}</span>
          <h3>{{ runtimeErrorTitle }}</h3>
          <p>{{ runtimeErrorDescription }}</p>
          <div class="onboarding-tour__missing-actions">
            <el-button class="onboarding-btn-secondary" @click="handleRetryRuntime">
              {{ t('onboarding.common.retry') }}
            </el-button>
            <el-button class="onboarding-btn-secondary" @click="handleSkipStep">
              {{ t('onboarding.common.skipStep') }}
            </el-button>
            <el-button class="onboarding-btn-primary" type="primary" @click="handlePause">
              {{ t('onboarding.common.openCenter') }}
            </el-button>
          </div>
        </template>

        <template v-else>
          <div class="onboarding-tour__body">
            <span
              class="onboarding-tour__readonly"
              :class="[
                `is-${dataAvailability.status}`,
                { 'is-simulate': !hasDataConstraint && onboardingStore.currentStep.interaction === 'simulate' },
              ]"
            >
              {{ hasDataConstraint
                ? dataStateBadge
                : onboardingStore.currentStep.interaction === 'simulate'
                  ? t('onboarding.tour.simulateBadge')
                  : t('onboarding.tour.readOnlyBadge') }}
            </span>
            <h3>{{ stepTitle }}</h3>
            <p>{{ stepDescription }}</p>
            <div v-if="isDeferredAvailability(dataAvailability)" class="onboarding-tour__data-note">
              {{ dataAvailability.status === 'empty-creatable'
                ? t('onboarding.dataState.emptyCreatable.note')
                : t('onboarding.dataState.emptyPrerequisite.note') }}
            </div>
            <div v-if="onboardingStore.currentStep.risk === 'external-link'" class="onboarding-tour__warning">
              {{ t('onboarding.tour.externalLinkWarning') }}
            </div>
            <div v-if="onboardingStore.currentStep.mobileNote" class="onboarding-tour__warning is-mobile">
              {{ onboardingStore.currentStep.mobileNote }}
            </div>
          </div>
          <footer class="onboarding-tour__actions">
            <el-button
              class="onboarding-btn-secondary"
              :disabled="progressBusy || onboardingStore.activeStepIndex === 0"
              @click="handlePrevious"
            >
              {{ t('onboarding.common.previous') }}
            </el-button>
            <span class="onboarding-tour__secondary">
              <template v-if="isDeferredAvailability(dataAvailability)">
                <el-button class="onboarding-btn-text" text :disabled="progressBusy" @click="handlePause">
                  {{ t('onboarding.dataState.backToTasks') }}
                </el-button>
                <el-button
                  class="onboarding-btn-primary"
                  type="primary"
                  :loading="progressBusy"
                  :disabled="progressBusy"
                  @click="handleNext"
                >
                  {{ t('onboarding.dataState.understood') }}
                </el-button>
              </template>
              <template v-else>
                <el-button class="onboarding-btn-text" text :disabled="progressBusy" @click="handleSkipStep">
                  {{ t('onboarding.common.skipStep') }}
                </el-button>
                <el-button
                  class="onboarding-btn-primary"
                  type="primary"
                  :loading="progressBusy"
                  :disabled="progressBusy"
                  @click="handleNext"
                >
                  {{ onboardingStore.activeStepIndex === onboardingStore.currentSteps.length - 1
                    ? t('onboarding.common.finishLearning')
                    : t('onboarding.common.next') }}
                </el-button>
              </template>
            </span>
          </footer>
        </template>
      </section>
    </div>
  </Teleport>
</template>

<style>
.onboarding-guide-target {
  box-shadow: none !important;
  filter: none !important;
}

.onboarding-tour {
  position: fixed;
  inset: 0;
  z-index: 34000;
  background: transparent;
}

.onboarding-tour.is-simulate {
  pointer-events: none;
}

.onboarding-tour.is-simulate .onboarding-tour__card {
  pointer-events: auto;
}

.onboarding-tour__mask {
  position: fixed;
  z-index: 1;
  background: rgb(15 23 42 / 68%);
}

.onboarding-tour__highlight {
  position: fixed;
  z-index: 2;
  background: transparent;
  border: 2px solid var(--el-color-primary);
  border-radius: 12px;
  box-shadow: none;
  transition: all 0.22s ease;
}

.onboarding-tour__card {
  position: fixed;
  z-index: 3;
  display: flex;
  flex-direction: column;
  width: 360px;
  overflow: visible;
  color: var(--el-text-color-primary);
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color);
  border-radius: 14px;
  box-shadow: none;
}

.onboarding-tour__card::before {
  position: absolute;
  top: 28px;
  z-index: 0;
  width: 12px;
  height: 12px;
  content: "";
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color);
  transform: rotate(45deg);
}

.onboarding-tour__card[data-placement="right"]::before {
  left: -7px;
}

.onboarding-tour__card[data-placement="left"]::before {
  right: -7px;
}

.onboarding-tour__card[data-placement="bottom"]::before {
  top: -7px;
  left: 32px;
}

.onboarding-tour__card[data-placement="top"]::before {
  top: auto;
  bottom: -7px;
  left: 32px;
}

.onboarding-tour__card[data-placement="center"]::before,
.onboarding-tour__card.is-missing::before {
  display: none;
}

.onboarding-tour__head {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  min-height: 54px;
  padding: 11px 14px 10px 16px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: var(--el-color-primary);
  background: var(--el-bg-color);
  border-bottom: 1px solid var(--el-border-color);
  border-radius: 14px 14px 0 0;
}

.onboarding-tour__body {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  padding: 17px 18px 18px;
  background: var(--el-bg-color);
}

.onboarding-tour__readonly {
  display: inline-flex;
  align-items: center;
  min-height: 23px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  line-height: 17px;
  color: var(--el-color-warning-dark-2);
  background: var(--el-color-warning-light-9);
  border-radius: 999px;
}

.onboarding-tour__readonly.is-simulate {
  color: #2563eb;
  background: #eff6ff;
}

.onboarding-tour__readonly.is-empty-creatable {
  color: #2563eb;
  background: #eff6ff;
}

.onboarding-tour__readonly.is-empty-prerequisite {
  color: var(--el-color-warning-dark-2);
  background: var(--el-color-warning-light-9);
}

.onboarding-tour__data-note {
  padding: 9px 10px;
  margin-top: 12px;
  font-size: 12px;
  line-height: 19px;
  color: var(--el-text-color-regular);
  background: var(--el-fill-color-light);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
}

.onboarding-tour__warning {
  padding: 9px 10px;
  margin-top: 12px;
  font-size: 12px;
  line-height: 19px;
  color: var(--el-color-warning-dark-2);
  background: var(--el-color-warning-light-9);
  border-radius: 8px;
}

.onboarding-tour__warning.is-mobile {
  display: none;
}

.onboarding-tour__card h3 {
  margin: 5px 0 7px;
  font-size: 17px;
  font-weight: 700;
  line-height: 24px;
  color: #0f2a58;
}

.onboarding-tour__card p {
  margin: 0;
  font-size: 13px;
  line-height: 21px;
  color: var(--el-text-color-primary);
}

.onboarding-tour__actions {
  position: relative;
  z-index: 1;
  border-radius: 0 0 14px 14px;
}

.onboarding-tour__secondary {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  align-items: center;
  white-space: nowrap;
}

.onboarding-tour__missing-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 20px;
}

.onboarding-tour__missing-actions .el-button {
  margin: 0;
}

.onboarding-tour__card.is-missing {
  width: min(440px, calc(100vw - 32px)) !important;
  padding: 22px;
}

.onboarding-tour__card .onboarding-btn-primary,
.onboarding-tour__card .onboarding-btn-secondary,
.onboarding-tour__card .onboarding-btn-text {
  min-height: 36px;
  padding: 0 18px;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  border-radius: 10px;
}

@media (width <= 767px) {
  .onboarding-tour__warning.is-mobile {
    display: block;
  }

  .onboarding-tour__card::before {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .onboarding-tour__highlight {
    transition: none;
  }
}
</style>
